import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ChevronRight, ArrowLeft, Plus, Mic, X } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const ESTADO_STYLES = {
    abierta: "bg-emerald-100 text-emerald-800",
    cerrada: "bg-gray-100 text-gray-600",
};

export default function EvaluacionOralLista() {
    const navigate = useNavigate();
    const [courses, setCourses] = useState([]);
    const [loadingCourses, setLoadingCourses] = useState(true);
    const [courseQuery, setCourseQuery] = useState("");
    const [selectedCourse, setSelectedCourse] = useState(null);

    const [assessments, setAssessments] = useState([]);
    const [loadingAssessments, setLoadingAssessments] = useState(false);
    const [showNew, setShowNew] = useState(false);

    useEffect(() => {
        api.get("/courses")
            .then((r) => setCourses(r.data || []))
            .catch(() => toast.error("Error cargando cursos"))
            .finally(() => setLoadingCourses(false));
    }, []);

    function loadAssessments(course) {
        setLoadingAssessments(true);
        api.get("/oral-assessments", { params: { course_id: course.id } })
            .then((r) => setAssessments(r.data || []))
            .catch(() => toast.error("Error cargando evaluaciones orales"))
            .finally(() => setLoadingAssessments(false));
    }

    function selectCourse(course) {
        setSelectedCourse(course);
        loadAssessments(course);
    }

    const filteredCourses = courses.filter((c) =>
        `${c.name} ${(c.sections || []).join(" ")}`.toLowerCase().includes(courseQuery.toLowerCase())
    );

    if (!selectedCourse) {
        return (
            <div className="p-6 md:p-10 fade-in min-h-screen max-w-2xl mx-auto">
                <h1 className="text-2xl font-semibold mb-1 inline-flex items-center gap-2"><Mic size={22} className="text-edu-navy" /> Evaluación Oral</h1>
                <p className="text-edu-muted mb-6">Elige un curso para crear o revisar prácticas de speaking / oral (Cambridge, Individual Oral del IB, etc.).</p>

                <div className="relative mb-4">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-edu-muted" />
                    <input
                        value={courseQuery}
                        onChange={(e) => setCourseQuery(e.target.value)}
                        placeholder="Buscar curso…"
                        className="edu-input w-full pl-9"
                        autoFocus
                    />
                </div>

                {loadingCourses ? (
                    <p className="text-sm text-edu-muted">Cargando…</p>
                ) : filteredCourses.length === 0 ? (
                    <div className="edu-card p-8 text-center text-edu-muted">No se encontraron cursos.</div>
                ) : (
                    <div className="space-y-2">
                        {filteredCourses.map((c) => (
                            <button
                                key={c.id}
                                onClick={() => selectCourse(c)}
                                className="edu-card w-full text-left p-4 hover:bg-edu-soft transition-colors flex items-center justify-between gap-3"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: c.color }} />
                                    <div>
                                        <p className="font-medium text-sm">{c.name}</p>
                                        <p className="text-xs text-edu-muted">{(c.sections || []).join(", ")}</p>
                                    </div>
                                </div>
                                <ChevronRight size={16} className="text-edu-muted" />
                            </button>
                        ))}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="p-6 md:p-10 fade-in min-h-screen max-w-2xl mx-auto">
            <button onClick={() => setSelectedCourse(null)} className="inline-flex items-center gap-2 text-sm text-edu-body hover:text-edu-navy mb-6">
                <ArrowLeft size={16} /> Volver a cursos
            </button>

            <div className="flex items-center justify-between mb-1">
                <h1 className="text-xl font-semibold">{selectedCourse.name}</h1>
                <button onClick={() => setShowNew(true)} className="edu-btn-primary px-3 py-2 text-xs inline-flex items-center gap-1.5">
                    <Plus size={14} /> Nueva evaluación
                </button>
            </div>
            <p className="text-edu-muted mb-6">{(selectedCourse.sections || []).join(", ")}</p>

            {loadingAssessments ? (
                <p className="text-sm text-edu-muted">Cargando…</p>
            ) : assessments.length === 0 ? (
                <div className="edu-card p-8 text-center text-edu-muted">Aún no hay evaluaciones orales para este curso.</div>
            ) : (
                <div className="space-y-2">
                    {assessments.map((a) => (
                        <button
                            key={a.id}
                            onClick={() => navigate(`/profesor/evaluacion-oral/${a.id}`)}
                            className="edu-card w-full text-left p-4 hover:bg-edu-soft transition-colors flex items-center justify-between gap-3"
                        >
                            <div>
                                <p className="font-medium text-sm">{a.titulo}</p>
                                <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full ${ESTADO_STYLES[a.estado] || ESTADO_STYLES.cerrada}`}>
                                    {a.estado === "abierta" ? "Habilitada" : "No habilitada"}
                                </span>
                            </div>
                            <ChevronRight size={16} className="text-edu-muted" />
                        </button>
                    ))}
                </div>
            )}

            {showNew && (
                <NewAssessmentModal
                    courseId={selectedCourse.id}
                    onClose={() => setShowNew(false)}
                    onCreated={(id) => navigate(`/profesor/evaluacion-oral/${id}`)}
                />
            )}
        </div>
    );
}

function NewAssessmentModal({ courseId, onClose, onCreated }) {
    const [titulo, setTitulo] = useState("");
    const [tiempoPorRespuesta, setTiempoPorRespuesta] = useState(120);
    const [duracionTotal, setDuracionTotal] = useState(10);
    const [saving, setSaving] = useState(false);

    async function create() {
        if (!titulo.trim()) {
            toast.error("Ponle un título a la evaluación");
            return;
        }
        setSaving(true);
        try {
            const { data } = await api.post("/oral-assessments", {
                course_id: courseId,
                titulo: titulo.trim(),
                tiempo_por_respuesta_seg: Number(tiempoPorRespuesta) || 120,
                duracion_total_min: Number(duracionTotal) || 10,
            });
            toast.success("Evaluación creada — ahora sube la rúbrica y el tema");
            onCreated(data.id);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error creando la evaluación");
        } finally {
            setSaving(false);
        }
    }

    return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="edu-card p-6 max-w-md w-full">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">Nueva evaluación oral</h3>
                    <button onClick={onClose}><X size={18} /></button>
                </div>
                <div className="space-y-3">
                    <div>
                        <span className="text-xs text-edu-muted font-semibold uppercase tracking-widest mb-1 block">Título</span>
                        <input value={titulo} onChange={(e) => setTitulo(e.target.value)} className="edu-input w-full" placeholder="Ej: Cambridge Speaking — Unidad 3" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <span className="text-xs text-edu-muted font-semibold uppercase tracking-widest mb-1 block">Tiempo por respuesta (seg)</span>
                            <input type="number" min="15" value={tiempoPorRespuesta} onChange={(e) => setTiempoPorRespuesta(e.target.value)} className="edu-input w-full" />
                        </div>
                        <div>
                            <span className="text-xs text-edu-muted font-semibold uppercase tracking-widest mb-1 block">Duración total (min)</span>
                            <input type="number" min="1" value={duracionTotal} onChange={(e) => setDuracionTotal(e.target.value)} className="edu-input w-full" />
                        </div>
                    </div>
                    <p className="text-xs text-edu-muted">Después de crearla podrás subir la rúbrica y escribir el tema/extracto. Solo se habilita para los alumnos cuando ambas cosas estén listas.</p>
                </div>
                <div className="flex justify-end gap-3 mt-5">
                    <button onClick={onClose} className="px-4 py-2 text-sm rounded-xl border border-edu">Cancelar</button>
                    <button onClick={create} disabled={saving} className="edu-btn-primary px-4 py-2 text-sm disabled:opacity-50">{saving ? "Creando…" : "Crear"}</button>
                </div>
            </div>
        </div>
    );
}
